import * as DialogPrimitive from "@radix-ui/react-dialog";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import type * as React from "react";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import {
	Dialog,
	DialogDescription,
	DialogOverlay,
	DialogPortal,
	DialogTitle,
} from "@/components/ui/dialog";
import { resolveMediaUrl } from "@/lib/utils";

export interface LightboxImage {
	src: string;
	alt: string;
}

export interface ImageLightboxProps {
	images: LightboxImage[];
	/** The index shown, or null when the viewer is closed. */
	openIndex: number | null;
	onOpenIndexChange: (index: number | null) => void;
}

const controlClassName =
	"flex h-11 w-11 items-center justify-center rounded-full bg-black/55 text-dashboard-on-fill transition-colors hover:bg-black/75 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black";

/**
 * A full-size image viewer on the Radix dialog, which traps focus while open
 * and closes on Escape. Previous and next wrap around; ArrowLeft and
 * ArrowRight do the same. On close, focus goes back to whatever element had it
 * when the viewer opened.
 */
export function ImageLightbox({
	images,
	openIndex,
	onOpenIndexChange,
}: ImageLightboxProps): React.ReactElement {
	const { t } = useTranslation();
	const openerRef = useRef<HTMLElement | null>(null);

	const count = images.length;
	const isOpen = openIndex !== null && openIndex >= 0 && openIndex < count;
	const current = isOpen ? images[openIndex] : null;

	function step(delta: number): void {
		if (openIndex === null || count === 0) return;
		onOpenIndexChange((openIndex + delta + count) % count);
	}

	function handleKeyDown(event: React.KeyboardEvent<HTMLDivElement>): void {
		if (event.key === "ArrowLeft") {
			event.preventDefault();
			step(-1);
		} else if (event.key === "ArrowRight") {
			event.preventDefault();
			step(1);
		}
	}

	return (
		<Dialog
			open={isOpen}
			onOpenChange={(open) => {
				if (!open) onOpenIndexChange(null);
			}}
		>
			<DialogPortal>
				<DialogOverlay className="bg-black/85" />
				<DialogPrimitive.Content
					className="fixed inset-0 flex items-center justify-center p-4 focus:outline-none sm:p-10"
					style={{ zIndex: 9999 }}
					onKeyDown={handleKeyDown}
					onClick={(event) => {
						// The content fills the screen, so a click on the dark area
						// around the photo lands here rather than on the overlay.
						if (event.target === event.currentTarget) onOpenIndexChange(null);
					}}
					onOpenAutoFocus={() => {
						// Radix fires this before it moves focus into the dialog.
						openerRef.current =
							document.activeElement instanceof HTMLElement
								? document.activeElement
								: null;
					}}
					onCloseAutoFocus={(event) => {
						event.preventDefault();
						openerRef.current?.focus();
						openerRef.current = null;
					}}
				>
					<DialogTitle className="sr-only">
						{t("imageLightbox.title")}
					</DialogTitle>
					<DialogDescription className="sr-only">
						{isOpen
							? t("imageLightbox.position", {
									current: openIndex + 1,
									total: count,
								})
							: null}
					</DialogDescription>

					{current && (
						<img
							src={resolveMediaUrl(current.src)}
							alt={current.alt}
							className="max-h-full max-w-full rounded-lg object-contain shadow-2xl"
						/>
					)}

					<p
						aria-hidden="true"
						className="absolute bottom-5 left-1/2 -translate-x-1/2 rounded-full bg-black/55 px-3 py-1 font-mono text-[11px] tracking-wider text-dashboard-on-fill"
					>
						{isOpen ? `${openIndex + 1} / ${count}` : null}
					</p>

					{count > 1 && (
						<>
							<button
								type="button"
								onClick={() => step(-1)}
								aria-label={t("imageLightbox.previous")}
								className={`${controlClassName} absolute left-3 top-1/2 -translate-y-1/2 sm:left-6`}
							>
								<ChevronLeft className="h-6 w-6" aria-hidden="true" />
							</button>
							<button
								type="button"
								onClick={() => step(1)}
								aria-label={t("imageLightbox.next")}
								className={`${controlClassName} absolute right-3 top-1/2 -translate-y-1/2 sm:right-6`}
							>
								<ChevronRight className="h-6 w-6" aria-hidden="true" />
							</button>
						</>
					)}

					<DialogPrimitive.Close
						aria-label={t("imageLightbox.close")}
						className={`${controlClassName} absolute right-3 top-3 sm:right-6 sm:top-6`}
					>
						<X className="h-5 w-5" aria-hidden="true" />
					</DialogPrimitive.Close>
				</DialogPrimitive.Content>
			</DialogPortal>
		</Dialog>
	);
}
